import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { improveApi } from '../../api/improve'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { factsOf, provenanceOf, ruleSentence, ruleTitle, SCOPE_BLURB, SCOPE_WORDS } from '../../lib/improve'
import { MIN_REASON } from '../../types/corrections'
import type { Rule } from '../../types/improve'

// Every rule the loop has written, as facts rather than JSON: what it says, whether it is a fact about one
// thing or a pattern, where it came from, and why. Retiring one is itself a correction, so it takes a
// reason — the next run reads that reason exactly like any other.

export function RulesPanel({ onOpenCorrection }: { onOpenCorrection: (correctionId: string) => void }) {
  const spoke = useSpoke()
  const [showRetired, setShowRetired] = useState(false)
  const q = useQuery({
    queryKey: ['improve-rules', spoke, showRetired],
    queryFn: () => improveApi.rules(spoke, showRetired ? undefined : true),
    staleTime: 30_000,
  })
  const rules = q.data ?? []
  const live = rules.filter((r) => r.active).length

  return (
    <div className="card rules-card">
      <div className="card-head">
        <div>
          <h2>Rules in force <span className="count">{live}</span></h2>
          <p className="muted">
            The typed definitions the loop is allowed to write. There is no path from a rule to a prompt: a rule is a
            row of a whitelisted kind, read by exactly one decider, and every one of them was replayed against the
            whole regression bank before it went live.
          </p>
        </div>
        <div className="actions">
          <button className="button small" onClick={() => setShowRetired((v) => !v)}>{showRetired ? 'Live only' : 'Include retired'}</button>
        </div>
      </div>

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p></div>}
      {q.isSuccess && rules.length === 0 && (
        <div className="empty-note">
          <strong>No rule has been written yet.</strong>
          <p className="muted">
            Rules come from two places: a nightly run that worked out what would have caught one of your corrections, or
            an improvement you state yourself below. Either way it is verified before it changes anything.
          </p>
        </div>
      )}

      <ul className="rule-list">
        {rules.map((r) => <RuleCard key={r.ruleId} rule={r} onOpenCorrection={onOpenCorrection} />)}
      </ul>
    </div>
  )
}

function RuleCard({ rule, onOpenCorrection }: { rule: Rule; onOpenCorrection: (correctionId: string) => void }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [retiring, setRetiring] = useState(false)
  const [reason, setReason] = useState('')
  const retire = useMutation({
    mutationFn: () => improveApi.retireRule(spoke, rule.ruleId, reason.trim()),
    onSuccess: () => {
      setRetiring(false); setReason('')
      void qc.invalidateQueries({ queryKey: ['improve-rules'] })
      void qc.invalidateQueries({ queryKey: ['improve'] })
    },
  })
  const from = provenanceOf(rule.provenance)
  const sentence = ruleSentence(rule.kind, rule.definition)
  const enough = reason.trim().length >= MIN_REASON

  return (
    <li className={`rule-card${rule.active ? '' : ' retired'}`}>
      <div className="rule-head">
        <div>
          <p className="kicker">{ruleTitle(rule)}{rule.version > 1 ? ` · version ${rule.version}` : ''}</p>
          <h3>{sentence ?? `A ${rule.kind} rule`}</h3>
        </div>
        <span className={`tag scope ${rule.scope}`} title={SCOPE_BLURB[rule.scope]}>{SCOPE_WORDS[rule.scope] ?? rule.scope}</span>
      </div>

      <dl className="facts compact">
        {factsOf(rule.definition).map((f) => (
          <Fragment key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></Fragment>
        ))}
      </dl>

      <p className="rule-because"><span className="kicker">Because</span> {rule.because}</p>
      <p className="rule-meta muted small">
        {from.kind === 'correction' ? 'From a correction' : from.kind === 'manual' ? 'Stated by hand' : from.kind === 'run' ? 'From a nightly run' : from.kind}
        {' · '}live since {formatDate(rule.createdAt)}
        {!rule.active && rule.retiredAt && ` · retired ${formatDate(rule.retiredAt)}`}
        {from.kind === 'correction' && from.id && (
          <> · <button className="link-button small" onClick={() => onOpenCorrection(from.id!)}>open the correction behind it</button></>
        )}
      </p>
      {!rule.active && rule.retiredWhy && <p className="rule-retired-why">Retired because: {rule.retiredWhy}</p>}

      {rule.active && !retiring && (
        <div className="actions">
          <button className="button small" onClick={() => setRetiring(true)}>Retire this rule</button>
        </div>
      )}
      {rule.active && retiring && (
        <div className="rule-retire">
          <textarea
            className="correct-reason" rows={2} value={reason} onChange={(ev) => setReason(ev.target.value)}
            placeholder="Why should this rule stop applying? Retiring a rule is itself a correction."
            aria-label="Reason for retiring (required)"
          />
          <p className="correct-note muted small">
            {enough
              ? 'The rule goes inactive immediately, and your reason lands on the ledger as a correction of its own.'
              : 'A reason is required: the next run reads it the same way it reads any other correction.'}
          </p>
          {retire.isError && <p className="error small">{retire.error instanceof RefusedError ? retire.error.refusal.message : (retire.error as Error).message}</p>}
          <div className="actions">
            <button className="button primary small" disabled={!enough || retire.isPending} onClick={() => retire.mutate()}>{retire.isPending ? 'Retiring…' : 'Retire it'}</button>
            <button className="button small" onClick={() => { setRetiring(false); setReason('') }}>Cancel</button>
          </div>
        </div>
      )}
    </li>
  )
}
